import { getAppStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { updateShowWelcomeChannelAction } from '../../home/actions/HomeActions';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { ServerSettings, ServerSettingsInstance } from '../../settings/services/ServerSettings';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { StartPageService } from '../../settings/services/StartPageService';
import { GeneralAuthSettingsProps } from '../../userSettings/components/GeneralAuthSettings';
import { LanguageAndRegionSettingsProps } from '../../userSettings/components/LanguageAndRegionSettings';
import { PreferenceSettingProps } from '../../userSettings/components/PreferenceSettings';
import { UserSettings } from '../../userSettings/services/resources/UserSettings';
import { UserSettingsActions } from '../../userSettings/services/UserSettingsActions';
import { FeatureFlags } from '../FeatureFlags';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { ISiteUserInfo } from '../models/ISiteUserInfo';
import { IUserSettings } from '../models/IUserSettings';
import { Module } from '../Module';
import { ModalService } from './ModalService';
import { ToasterService, ToastType } from './ToasterService';

export class UserSettingsCtrlCommon {
  private serverSettings: ServerSettings;
  private sessionManagementService: SessionManagementService;

  public static $inject = [
    '$q',
    '$translate',
    'ModalService',
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private modal: ModalService,
  ) {
    this.serverSettings = ServerSettingsInstance;
    this.sessionManagementService = getSessionManagementServiceInstance();
  }

  private scope: UserSettingsCtrlCommon.Scope;
  private boundOnAppStateChanged = this.onAppStateChanged.bind(this);
  private toaster = ToasterService.instance;

  public initScope(
    scope: UserSettingsCtrlCommon.Scope,
    isViewerSelf: boolean,
    serverUser: IServerUserInfo,
    siteUser: ISiteUserInfo,
    userSettings: IUserSettings,
    userRefreshTokenCount: number): void {

    this.scope = scope;

    // setup change listener for user settings
    this.setupUserSettingsChangeListener();

    scope.server = this.sessionManagementService;

    scope.isDomainFamilyAD = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
    scope.isDomainFamilyExternal = ServerSettingsUnauthenticated.isServerDomainFamilyExternal();
    scope.isDomainFamilyLocal = ServerSettingsUnauthenticated.isServerDomainFamilyLocal();

    scope.isViewerSelf = isViewerSelf;
    scope.isViewerServerAdmin = this.sessionManagementService.isSessionServerAdmin();
    const isViewerAdmin = this.sessionManagementService.isSessionSiteOrServerAdmin();
    scope.isViewerAdmin = isViewerAdmin;

    scope.changePasswordUrl = this.sessionManagementService.changePasswordUrl(); // might be undefined
    scope.manageMfaUrl = (!!siteUser && !!siteUser.authenticationProviderId
      && siteUser.authenticationProviderId === 'TABID_WITH_MFA') ? this.sessionManagementService.manageMfaUrl() : '';
    scope.updateDisplayNameAllowed = this.sessionManagementService.updateDisplayNameAllowed();

    scope.userSettings = userSettings || {};
    scope.serverUser = serverUser;

    scope.controls = {};

    // section: General Auth Settings
    scope.generalAuthSettingsProps = {
      isDomainFamilyLocal: scope.isDomainFamilyLocal,
      isDomainFamilyExternal: scope.isDomainFamilyExternal,
      isDomainFamilyAD: scope.isDomainFamilyAD,
      emailFormInitialValues: {
        email: scope.userSettings.email,
        displayName: serverUser.displayName
      },
      updateDisplayNameAllowed: scope.updateDisplayNameAllowed,
      siteUser: scope.siteUser,
      serverUser: scope.serverUser,
      isViewerServerAdmin: scope.isViewerServerAdmin,
      isViewerSelf: scope.isViewerSelf,
      changePasswordUrl: scope.changePasswordUrl,
      isUserSelf: scope.server.isSessionUserSelf(scope.serverUser.username, scope.serverUser.domainName),
    };

    scope.languageAndRegionSettingsProps = {
      languageInitialValues: {language: scope.userSettings.language},
      localeInitialValues: {locale: scope.userSettings.locale},
      timeZoneInitialValues: {timeZoneId: scope.userSettings.timeZoneId},
      serverUser: scope.serverUser,
      isServerUserSubplace: !scope.siteUser
    };

    // TODO: most of this logic should be encapsulated elsewhere, like in the StartPageService
    scope.updateUserStartPage = (startPage: string) => {
      // Show server default start page if not set by user
      if (!startPage) {
        // Start page from sessionInfo
        startPage = this.sessionManagementService.getStartPage();
      }

      const currentSiteUrlName = this.sessionManagementService.sessionSiteUrlName();
      scope.startPageText = startPage;
      scope.startPageHref = StartPageService.getStartPageForSite(startPage, currentSiteUrlName);
    };

    scope.updateUserStartPage(scope.userSettings.startPage);

    //Section: Refresh Failure Notification Setting
    scope.siteUser = siteUser;
    scope.origNotificationSetting = siteUser && siteUser.notificationEnabled;

    // settings checkboxes and save buttons are disabled if the user does not have a record on the current site
    // this occurs when server admins attempt to change their site-specific settings without having content on that site
    scope.userHasNoSiteId = () => {
      return (!siteUser || !siteUser.id);
    };

    scope.isNotificationEnabledForServer =
      this.sessionManagementService.isNotificationEnabledForServer();
    scope.isNotificationEnabledForSite =
      (!scope.userHasNoSiteId()) && this.sessionManagementService.isNotificationEnabledForSite();

    // the save button is disabled if the current value of the checkbox is different from the value saved in server
    scope.isUpdateNotificationSettingDisabled = () => {
      return (scope.userHasNoSiteId() || scope.origNotificationSetting === siteUser.notificationEnabled);
    };

    scope.updateNotificationSettingBusy = false;
    scope.updateNotificationSetting = () => {
      scope.updateNotificationSettingBusy = true;
      UserSettings.updateNotificationSetting(
        siteUser.id, siteUser.notificationEnabled)
        .then((result: IResult) => {
          this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
          scope.origNotificationSetting = siteUser.notificationEnabled;
          scope.updateNotificationSettingBusy = false;
        }).catch((error: IError) => this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()}));
    };

    scope.initialWelcomeChannelEnabled = this.sessionManagementService.isWelcomeChannelEnabledForUser();
    scope.userSettings.welcomeChannelEnabled = this.sessionManagementService.isWelcomeChannelEnabledForUser();
    scope.isWelcomeChannelEnabledPristine = () => {
      // TFS 1092051: For some reason, scope.userSettings.welcomeChannelEnabled can become undefined if another change happens on the scope without reloading.
      // In this case, default to the initial value that was on the server.
      if (scope.userSettings.welcomeChannelEnabled === undefined) {
        scope.userSettings.welcomeChannelEnabled = this.sessionManagementService.isWelcomeChannelEnabledForUser();
      }
      return scope.userSettings.welcomeChannelEnabled === scope.initialWelcomeChannelEnabled;
    };
    scope.updateWelcomeChannelEnabledBusy = false;
    scope.updateWelcomeChannelEnabled = () => {
      scope.updateWelcomeChannelEnabledBusy = true;
      UserSettings.updateWelcomeChannelEnabled(scope.userSettings.welcomeChannelEnabled)
        .then(() => {
          this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess') });
          scope.updateWelcomeChannelEnabledBusy = false;
          scope.initialWelcomeChannelEnabled = scope.userSettings.welcomeChannelEnabled;
          // Update the redux store so we don't need to make a getSessionInfoCall
          const updateWelcomeChannelAction = updateShowWelcomeChannelAction(scope.userSettings.welcomeChannelEnabled);
          getAppStore().dispatch(updateWelcomeChannelAction);
        }).catch(() => this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure() }));
    };

    // Suspended Subscription Notification section
    if (FeatureFlags.suspendSubscriptions) {
      scope.isSuspendedSubscriptionVisible = () => {
        return isViewerAdmin && scope.isViewerSelf;
      };
      if (scope.isSuspendedSubscriptionVisible()) {
        // get the user's preference saved in server
        UserSettings.getSuspendedSubscriptionSetting(this.scope.serverUser.id)
        .then((result: boolean) => {
          scope.origSuspendedSubscriptionSetting = result;
          scope.userSettings.suspendedSubscriptionSetting = result;
        }).catch((error: IError) => { });

        scope.isSuspendedSubscriptionSettingBusy = false;

        // update the value saved in server
        scope.updateSuspendedSubscriptionSetting = () => {
          // disable the save button while waiting for response
          scope.isSuspendedSubscriptionSettingBusy = true;

          UserSettings.updateSuspendedSubscriptionSetting(
            siteUser.id,
            scope.userSettings.suspendedSubscriptionSetting
          ).then((result: IResult) => {
            this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
            scope.origSuspendedSubscriptionSetting = scope.userSettings.suspendedSubscriptionSetting;
            scope.isSuspendedSubscriptionSettingBusy = false;
          }).catch((error: IError) => {
            scope.isSuspendedDataAlertSettingBusy = false;
            this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
          });
        };

        // the save button is disabled if the current value of the checkbox is different from the value saved in server
        scope.isSaveSuspendedSubscriptionSettingDisabled = () => {
          return scope.userHasNoSiteId() || scope.origSuspendedSubscriptionSetting === scope.userSettings.suspendedSubscriptionSetting;
        };
      }
    }

    // Suspended Data Alert Notification section
    scope.isSuspendedDataAlertVisible = () => {
      return isViewerAdmin && scope.isViewerSelf;
    };
    if (scope.isSuspendedDataAlertVisible()) {
      // get the user's preference saved in server
      UserSettings.getSuspendedDataAlertSetting(this.scope.serverUser.id)
      .then((result: boolean) => {
        scope.origSuspendedDataAlertSetting = result;
        scope.userSettings.suspendedDataAlertSetting = result;
      }).catch((error: IError) => { });

      scope.isSuspendedDataAlertSettingBusy = false;

      // update the value saved in server
      scope.updateSuspendedDataAlertSetting = () => {
        // disable the save button while waiting for response
        scope.isSuspendedDataAlertSettingBusy = true;

        UserSettings.updateSuspendedDataAlertSetting(
          siteUser.id,
          scope.userSettings.suspendedDataAlertSetting
        ).then((result: IResult) => {
          this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
          scope.origSuspendedDataAlertSetting = scope.userSettings.suspendedDataAlertSetting;
          scope.isSuspendedDataAlertSettingBusy = false;
        }).catch((error: IError) =>  {
          scope.isSuspendedDataAlertSettingBusy = false;
          this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
        });
      };

      // the save button is disabled if the current value of the checkbox is different from the value saved in server
      scope.isSaveSuspendedDataAlertSettingDisabled = () => {
        return scope.userHasNoSiteId() || scope.origSuspendedDataAlertSetting === scope.userSettings.suspendedDataAlertSetting;
      };
    }

    if (FeatureFlags.markAnimationServer) {
      scope.isMarkAnimationVisible = () => {
        return scope.isViewerSelf;
      };
      if (scope.isMarkAnimationVisible()) {
        // get the user's preference saved in server
        UserSettings.getMarkAnimationSetting(this.scope.serverUser.id)
        .then((result: boolean) => {
          scope.origMarkAnimationSetting = result;
          scope.userSettings.markAnimationSetting = result;
        }).catch((error: IError) => { });

        scope.isMarkAnimationSettingBusy = false;

        // update the value saved in server
        scope.updateMarkAnimationSetting = () => {
          // disable the save button while waiting for response
          scope.isMarkAnimationSettingBusy = true;

          UserSettings.updateMarkAnimationSetting(
            siteUser.id,
            scope.userSettings.markAnimationSetting
          ).then((result: IResult) => {
            this.toaster.pop({ type: ToastType.Success, text: this.$translate.instant('settingsUpdateSuccess')});
            scope.origMarkAnimationSetting = scope.userSettings.markAnimationSetting;
            scope.isMarkAnimationSettingBusy = false;
          }).catch((error: IError) =>  {
            scope.isMarkAnimationSettingBusy = false;
            this.toaster.pop({ type: ToastType.Error, text: Messages.settingsUpdateFailure()});
          });
        };

        // the save button is disabled if the current value of the checkbox is the same as what's saved in server
        scope.isSaveMarkAnimationSettingDisabled = () => {
          return scope.userHasNoSiteId() || scope.origMarkAnimationSetting === scope.userSettings.markAnimationSetting;
        };

        scope.preferenceSettingProps = {
          isViewerSelf: scope.isViewerSelf,
          userHasNoSiteId: scope.userHasNoSiteId(),
          siteUser: scope.siteUser,
          welcomeChannelEnabled: scope.userSettings.welcomeChannelEnabled,
          startPage: scope.userSettings.startPage,
          sessionSiteUrlName: this.sessionManagementService.sessionSiteUrlName(),
          isViewerAdmin: scope.isViewerAdmin,
          serverUser: scope.serverUser,
          isServerUserSubplace: !scope.siteUser
        };
      }
    }
  }

  private updateUserSettingsOnScope(userSettings: IUserSettings) {
    this.scope.userSettings = userSettings || {};
    this.scope.updateUserStartPage(this.scope.userSettings.startPage);
  }

  private onAppStateChanged() {
    const userSettingsState = getAppStore().getState().userSettings;
    const domainMap = userSettingsState[this.scope.serverUser.domainName];
    if (domainMap) {
      const userSettings = domainMap[this.scope.serverUser.username];
      if (userSettings !== this.scope.userSettings) {
        this.updateUserSettingsOnScope(userSettings);
      }
    }
  }

  private setupUserSettingsChangeListener() {
    const unsubscribe = getAppStore().subscribe(this.boundOnAppStateChanged);
    this.scope.$on('$destroy', () => {
      unsubscribe();
      UserSettingsActions.forgetUserSettings(this.scope.serverUser.username, this.scope.serverUser.domainName);
    });
  }

}

export module UserSettingsCtrlCommon {

  export interface Scope extends ng.IScope {
    serverUser: IServerUserInfo;
    isDomainFamilyAD: boolean;
    isDomainFamilyExternal: boolean;
    isDomainFamilyLocal: boolean;
    isLocalAuth: boolean;
    isViewerSelf: boolean;
    isViewerAdmin: boolean;
    isViewerServerAdmin: boolean;
    userSettings: IUserSettings;
    startPageText: string;
    startPageHref: string;
    welcomeChannelEnabled: boolean;
    updateWelcomeChannelEnabled(): void;
    updateWelcomeChannelEnabledBusy: boolean;
    initialWelcomeChannelEnabled: boolean;
    isWelcomeChannelEnabledPristine(): boolean;
    userSettingsUpdated(): void;
    onAfterStartPageReset(): void;
    changePasswordUrl: string;
    updateDisplayNameAllowed: boolean;
    updateUserStartPage(startPage: string): void;
    controls: {};
    generalAuthSettingsProps: GeneralAuthSettingsProps;
    languageAndRegionSettingsProps: LanguageAndRegionSettingsProps;
    isNotificationEnabledForServer: boolean;
    isNotificationEnabledForSite: boolean;
    siteUser: ISiteUserInfo;
    userHasNoSiteId(): boolean;
    origNotificationSetting: boolean;
    isUpdateNotificationSettingDisabled(): boolean;
    updateNotificationSetting(): void;
    updateNotificationSettingBusy: boolean;
    server: SessionManagementService;
    isSuspendedSubscriptionVisible(): boolean;
    suspendedSubscriptionSetting: boolean;
    updateSuspendedSubscriptionSetting(): void;
    origSuspendedSubscriptionSetting: boolean;
    isSuspendedSubscriptionSettingBusy: boolean;
    isSaveSuspendedSubscriptionSettingDisabled(): boolean;
    isSuspendedDataAlertVisible(): boolean;
    suspendedDataAlertSetting: boolean;
    updateSuspendedDataAlertSetting(): void;
    origSuspendedDataAlertSetting: boolean;
    isSuspendedDataAlertSettingBusy: boolean;
    isSaveSuspendedDataAlertSettingDisabled(): boolean;
    isMarkAnimationVisible(): boolean;
    markAnimationSetting: boolean;
    updateMarkAnimationSetting(): void;
    origMarkAnimationSetting: boolean;
    isMarkAnimationSettingBusy: boolean;
    isSaveMarkAnimationSettingDisabled(): boolean;
    preferenceSettingProps: PreferenceSettingProps;
    manageMfaUrl: string;
  }

}

Module.instance().service('UserSettingsCtrlCommon', UserSettingsCtrlCommon);
